import React, { useState, useEffect } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { useNavigate } from "react-router-dom";
import { Grid, TextField, Typography } from "@mui/material";
import NavbarMonitoring from "../components/NavbarMonitoring";
import { AppColors } from "../resources/AppColors";
import CardMonitoring from "../components/CardMonitoring";
import { CommonTexts } from "../locale/es";
import TextMonitoring from "../components/TextMonitoring";
import { textType } from "../resources/AppTexts";
import ButtonMonitoring from "../components/ButtonMonitoring";
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "redux/reducers/userAuthenticationReducer";
import Loader from "components/Loader/Loader";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

const useStyles = makeStyles((theme) => ({
  subText: {
    color: AppColors.SUBTEXT,
  },
  button: {
    textTransform: "none",
    color: AppColors.PRIMARY,
  },
  title: {
    color: AppColors.PRIMARY,
  },
  background: {
    backgroundColor: AppColors.BACKGROUND,
  },
  backgroundBox: {
    backgroundColor: AppColors.WHITE,
  },
  colorLabel: {
    color: AppColors.PRIMARY,
    opacity: "0,2",
  },
  textField: {
    border: "1px solid blue",
    borderRadius: "8px",
  },
  textFieldLabel: {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: AppColors.PRIMARY,
      },
    },
    "& .MuiInputBase-root": {
      color: AppColors.PRIMARY,
    },
    "& .MuiInputLabel-root": {
      color: AppColors.PRIMARY,
    },
    color: AppColors.PRIMARY,
    backgroundColor: AppColors.BOX_FILL,
  },
}));

const LoginSchema = z.object({
  username: z.string().min(1, "Campo requerido").email({ message: "Debe ser un email" }),
  password: z.string().min(1, "Campo requerido")
})

export default function LoginPage() {
  const [formError, setFormError] = useState("");

  const { handleSubmit, control } = useForm({
    mode: "onBlur",
    reValidateMode: "onChange",
    resolver: zodResolver(LoginSchema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const navigate = useNavigate();
  const classes = useStyles();

  const { user, isAuthenticated } = useSelector(state => state.userAuthentication)

  const dispatch = useDispatch()

  useEffect(() => {
    if (user && isAuthenticated) {
      navigate("/dades-del-participant");
    }
  }, [navigate, user, isAuthenticated]);

  async function handleLogin(data) {
    try {
      // * Need to await for the promise to resolve or to throw
      await dispatch(loginUser(data));

    } catch (error) {
      const isIncorrectPassword = error.response.statusText = "Unauthorized"

      if (isIncorrectPassword) {
        setFormError(error.response.statusText);
      } else {
        setFormError(error)
      }
    }
  };

  if (user && isAuthenticated) {
    return <Loader message={<Typography variant="title">{CommonTexts.LOADING_APP}</Typography>} pt={32} />
  }

  return (
    <Grid container alignItems='center' direction='column'>
      <Grid item>
        <NavbarMonitoring />
      </Grid>
      <Grid
        item
        component="form"
        onSubmit={handleSubmit((data) => handleLogin(data))}
        style={{ marginTop: "15vh", marginBottom: "15vh" }}
      >
        <CardMonitoring
          bg={AppColors.WHITE}
          children={
            <Grid
              container
              item
              alignItems='center'
              direction='column'
              justifyContent='center'
            >
              <div
                style={{
                  margin: "5vh",
                  display: "flex",
                  flexDirection: "column",
                  minWidth: 300,
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <TextMonitoring
                    type={textType.TITLE_MEDIUM}
                    text={CommonTexts.WELCOME_BACK_UPPER}
                  />
                </div>
                <div style={{ textAlign: "center", height: 40 }} />
                <Controller
                  control={control}
                  name='username'
                  render={({
                    field,
                    fieldState: { error },
                  }) => (
                    <TextField
                      {...field}
                      type='text'
                      InputLabelProps={{
                        required: true
                      }}
                      label={CommonTexts.USERNAME}
                      margin='normal'
                      variant='outlined'
                      autoFocus
                      className={classes.textFieldLabel}
                      error={Boolean(error) || formError === "Unauthorized"}
                      helperText={error?.message ?? ""}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name='password'
                  render={({
                    field,
                    fieldState: { error },
                  }) => (
                    <TextField
                      {...field}
                      type='password'
                      label={CommonTexts.PASSWORD}
                      InputLabelProps={{
                        required: true,
                        shrink: true
                      }}
                      margin='normal'
                      variant='outlined'
                      autoComplete="current-password"
                      className={classes.textFieldLabel}
                      error={Boolean(error) || formError === "Unauthorized"}
                      helperText={error?.message ?? ""}
                    />
                  )}
                />
                <Typography mt={1} variant='body' sx={{ color: "red" }}>
                  {formError.response && "No es posible iniciar sesión en este momento"}
                  {formError === "Unauthorized" && "Usuario o contraseña incorrectos"}
                </Typography>
                <TextMonitoring
                  color={AppColors.SUBTEXT}
                  type={textType.BODY}
                  text={CommonTexts.INFO_LOGIN}
                />
                <Grid
                  container
                  wrap='nowrap'
                  spacing={4}
                  direction='column'
                  alignItems='center'
                  justifyContent='center'
                >
                  <Grid
                    container
                    item
                    alignItems='center'
                    justifyContent='center'
                    sx={{ mt: 2 }}
                  >
                    <ButtonMonitoring
                      text={CommonTexts.LOGIN}
                      type="submit"
                      onClick={handleSubmit}
                    />
                  </Grid>
                </Grid>
              </div>
            </Grid>
          }
        />
      </Grid>
    </Grid>
  );
}
